/**
 * Papéis de um membro dentro de um workspace (seção 3 da especificação).
 * A ordem do array é a ordem hierárquica: quem está antes pode tudo que quem está depois pode.
 */
export const WORKSPACE_ROLES = ["dono", "admin", "gestor", "analista", "cliente_final"] as const;
export type WorkspaceRole = (typeof WORKSPACE_ROLES)[number];

export const ROLE_LABELS: Record<WorkspaceRole, string> = {
  dono: "Dono",
  admin: "Admin",
  gestor: "Gestor de tráfego",
  analista: "Analista",
  cliente_final: "Cliente final",
};

export const ROLE_DESCRIPTIONS: Record<WorkspaceRole, string> = {
  dono: "Controle total, incluindo faturamento e exclusão do workspace.",
  admin: "Gerencia membros, clientes e contas. Não mexe no faturamento.",
  gestor: "Cria e edita campanhas nos clientes em que tem acesso.",
  analista: "Só leitura: vê campanhas e relatórios.",
  cliente_final: "Vê apenas os relatórios do próprio cliente.",
};

/** Papéis que um convite pode atribuir (ninguém convida outro Dono). */
export const INVITABLE_ROLES = [
  "admin",
  "gestor",
  "analista",
  "cliente_final",
] as const satisfies readonly WorkspaceRole[];
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

/** Permissões nomeadas usadas pela API e pela interface. */
export const PERMISSIONS = [
  "workspace:read",
  "workspace:update",
  "workspace:delete",
  "members:read",
  "members:invite",
  "members:update_role",
  "members:remove",
  "audit:read",
  "meta:read",
  "meta:manage",
  "meta:sync",
  /** Gestão de BM (pessoas, parceiros, system users), criar contas e BMs. */
  "bm:manage",
  /** Editar nome e limite de gasto de uma conta de anúncio. */
  "adaccounts:edit",
  "clients:read",
  "clients:manage",
  "notifications:read",
  "campaigns:read",
  "campaigns:write",
  "reports:read",
  /** Fase 7: regras automáticas (ver / criar e editar). */
  "rules:read",
  "rules:write",
  /** Fase 7: leads (ver / formulários, destinos, status, backfill). */
  "leads:read",
  "leads:write",
  /** Fase 7: webhooks de saída e retenção de leads do workspace. */
  "alerts:manage",
  /** Fase 8: Assistente de IA — analisar/diagnosticar (ai:use) e gerar variações, estrutura e regras (ai:write). */
  "ai:use",
  "ai:write",
  /** Fase 9: ver o plano e a cobrança (billing:read) e assinar/trocar/cancelar (billing:manage). */
  "billing:read",
  "billing:manage",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ROLE_PERMISSIONS: Record<WorkspaceRole, readonly Permission[]> = {
  dono: PERMISSIONS,
  admin: [
    "workspace:read",
    "workspace:update",
    "members:read",
    "members:invite",
    "members:update_role",
    "members:remove",
    "audit:read",
    "meta:read",
    "meta:manage",
    "meta:sync",
    "bm:manage",
    "adaccounts:edit",
    "clients:read",
    "clients:manage",
    "notifications:read",
    "campaigns:read",
    "campaigns:write",
    "reports:read",
    "rules:read",
    "rules:write",
    "leads:read",
    "leads:write",
    "alerts:manage",
    "ai:use",
    "ai:write",
    "billing:read",
  ],
  gestor: [
    "workspace:read",
    "members:read",
    "meta:read",
    "meta:sync",
    "adaccounts:edit",
    "clients:read",
    "notifications:read",
    "campaigns:read",
    "campaigns:write",
    "reports:read",
    "rules:read",
    "rules:write",
    "leads:read",
    "leads:write",
    "ai:use",
    "ai:write",
  ],
  analista: [
    "workspace:read",
    "members:read",
    "meta:read",
    "clients:read",
    "notifications:read",
    "campaigns:read",
    "reports:read",
    "rules:read",
    "leads:read",
    "ai:use",
  ],
  cliente_final: [
    "workspace:read",
    "meta:read",
    "clients:read",
    "notifications:read",
    "campaigns:read",
    "reports:read",
    "rules:read",
    "leads:read",
  ],
};

export function roleHas(role: WorkspaceRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

export function roleRank(role: WorkspaceRole): number {
  return WORKSPACE_ROLES.indexOf(role);
}

/** Um papel só pode gerenciar papéis estritamente abaixo dele na hierarquia. */
export function canManageRole(actor: WorkspaceRole, target: WorkspaceRole): boolean {
  return roleRank(actor) < roleRank(target);
}

/**
 * Papéis que podem ser restritos a clientes específicos (seção 3: "gestor X só nos clientes A e B").
 * Dono e Admin sempre veem tudo; Cliente final só vê os clientes aos quais está vinculado.
 */
export const CLIENT_RESTRICTABLE_ROLES = [
  "gestor",
  "analista",
  "cliente_final",
] as const satisfies readonly WorkspaceRole[];

export function roleCanBeRestrictedToClients(role: WorkspaceRole): boolean {
  return (CLIENT_RESTRICTABLE_ROLES as readonly WorkspaceRole[]).includes(role);
}
